import {
  Children,
  isValidElement,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

/** Мелкие примитивы интерфейса — чтобы не размазывать одни и те же классы по экранам. */

const FIELD =
  'w-full rounded-lg border border-neutral-300 bg-white px-3 py-2 text-sm outline-none transition focus:border-neutral-900 disabled:opacity-50 dark:border-neutral-700 dark:bg-neutral-950 dark:focus:border-neutral-300';

export function Input(props: React.InputHTMLAttributes<HTMLInputElement>) {
  return <input {...props} className={`${FIELD} ${props.className ?? ''}`} />;
}

export function Textarea(props: React.TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea {...props} className={`${FIELD} ${props.className ?? ''}`} />;
}

export function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <label className="block space-y-1.5">
      <span className="text-sm font-medium">{label}</span>
      {children}
      {hint && <span className="block text-xs text-neutral-500">{hint}</span>}
    </label>
  );
}

// ───────────────────────────── Служебное ─────────────────────────────

/** Закрывает всплывающий слой по клику вне его и по Escape. */
function useDismiss(open: boolean, onClose: () => void) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;

    const onPointer = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) onClose();
    };
    const onKey = (event: KeyboardEvent) => event.key === 'Escape' && onClose();

    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);

    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  return ref;
}

const POPOVER =
  'absolute z-50 mt-1 overflow-auto rounded-xl border border-neutral-200 bg-white p-1 shadow-lg dark:border-neutral-700 dark:bg-neutral-900';

// ────────────────────────────── Селект ──────────────────────────────

interface Option {
  value: string;
  label: ReactNode;
}

/**
 * Свой список вместо нативного `<select>`: тот не поддаётся оформлению
 * и в тёмной теме раскрывается системным белым списком.
 *
 * Принимает те же `<option>` в детях и тот же обработчик, что и раньше,
 * поэтому места вызова не переписывались.
 */
export function Select({
  value,
  onChange,
  children,
  disabled,
  className,
  placeholder = 'Не выбрано',
}: {
  value?: string | number;
  onChange?: React.ChangeEventHandler<HTMLSelectElement>;
  children?: ReactNode;
  disabled?: boolean;
  className?: string;
  placeholder?: string;
}) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const ref = useDismiss(open, () => setOpen(false));

  const options = useMemo<Option[]>(
    () =>
      Children.toArray(children)
        .filter((child): child is React.ReactElement<{ value?: string; children?: ReactNode }> =>
          isValidElement(child),
        )
        .map((child) => ({
          value: String(child.props.value ?? ''),
          label: child.props.children ?? '',
        })),
    [children],
  );

  const current = options.find((option) => option.value === String(value ?? ''));

  const pick = (option: Option) => {
    setOpen(false);
    // Синтетическое событие нативной формы — обработчики на местах вызова не меняются
    onChange?.({
      target: { value: option.value },
    } as unknown as React.ChangeEvent<HTMLSelectElement>);
  };

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (disabled) return;

    if (!open && (event.key === 'Enter' || event.key === ' ' || event.key === 'ArrowDown')) {
      event.preventDefault();
      setActive(
        Math.max(
          0,
          options.findIndex((o) => o.value === String(value ?? '')),
        ),
      );
      setOpen(true);
      return;
    }

    if (!open) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActive((i) => Math.min(options.length - 1, i + 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActive((i) => Math.max(0, i - 1));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const option = options[active];
      if (option) pick(option);
    }
  };

  return (
    <div ref={ref} className={`relative ${className ?? ''}`}>
      <button
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => !disabled && setOpen((v) => !v)}
        onKeyDown={onKeyDown}
        className={`${FIELD} flex items-center justify-between gap-2 text-left ${
          open ? 'border-neutral-900 dark:border-neutral-300' : ''
        }`}
      >
        <span className={`truncate ${current ? '' : 'text-neutral-400'}`}>
          {current?.label ?? placeholder}
        </span>
        <span
          className={`shrink-0 text-[10px] text-neutral-400 transition ${open ? 'rotate-180' : ''}`}
        >
          ▼
        </span>
      </button>

      {open && (
        <div className={`${POPOVER} max-h-64 w-full`} role="listbox">
          {options.length === 0 && (
            <p className="px-3 py-2 text-sm text-neutral-400">Нет вариантов</p>
          )}

          {options.map((option, index) => {
            const selected = option.value === String(value ?? '');

            return (
              <button
                key={`${option.value}-${index}`}
                type="button"
                role="option"
                aria-selected={selected}
                onMouseEnter={() => setActive(index)}
                onClick={() => pick(option)}
                className={`flex w-full items-center justify-between gap-2 rounded-lg px-3 py-2 text-left text-sm transition ${
                  index === active ? 'bg-neutral-100 dark:bg-neutral-800' : ''
                } ${selected ? 'font-medium' : ''}`}
              >
                <span className="truncate">{option.label}</span>
                {selected && <span className="shrink-0 text-xs">✓</span>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ──────────────────────────── Выбор даты ────────────────────────────

const WEEKDAYS = ['пн', 'вт', 'ср', 'чт', 'пт', 'сб', 'вс'];
const MONTHS = [
  'Январь',
  'Февраль',
  'Март',
  'Апрель',
  'Май',
  'Июнь',
  'Июль',
  'Август',
  'Сентябрь',
  'Октябрь',
  'Ноябрь',
  'Декабрь',
];

const iso = (date: Date): string => date.toISOString().slice(0, 10);
const humanDate = (value: string): string => (value ? value.split('-').reverse().join('.') : '');

/**
 * Календарь вместо `<input type="date">`: нативный виджет выглядит по-разному
 * в каждом браузере и не подчиняется теме приложения.
 * Значение и обработчик — как у обычного поля, в формате `YYYY-MM-DD`.
 */
export function DateInput({
  value,
  onChange,
  disabled,
  className,
  placeholder = 'дд.мм.гггг',
}: {
  value?: string;
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
  disabled?: boolean;
  className?: string;
  placeholder?: string;
}) {
  const [open, setOpen] = useState(false);
  const ref = useDismiss(open, () => setOpen(false));

  const selected = value && /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : '';
  const [cursor, setCursor] = useState(() =>
    selected ? new Date(`${selected}T00:00:00Z`) : new Date(),
  );

  const emit = (next: string) => {
    setOpen(false);
    onChange?.({ target: { value: next } } as unknown as React.ChangeEvent<HTMLInputElement>);
  };

  const year = cursor.getUTCFullYear();
  const month = cursor.getUTCMonth();
  const daysInMonth = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  // Неделя начинается с понедельника
  const leading = (new Date(Date.UTC(year, month, 1)).getUTCDay() + 6) % 7;

  const cells: Array<string | null> = [
    ...Array.from({ length: leading }, () => null),
    ...Array.from(
      { length: daysInMonth },
      (_, i) => `${year}-${String(month + 1).padStart(2, '0')}-${String(i + 1).padStart(2, '0')}`,
    ),
  ];

  const today = iso(new Date());
  const shiftMonth = (delta: number) => setCursor(new Date(Date.UTC(year, month + delta, 1)));

  return (
    <div ref={ref} className={`relative ${className ?? ''}`}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setOpen((v) => !v)}
        className={`${FIELD} flex items-center justify-between gap-2 text-left ${
          open ? 'border-neutral-900 dark:border-neutral-300' : ''
        }`}
      >
        <span className={selected ? '' : 'text-neutral-400'}>
          {selected ? humanDate(selected) : placeholder}
        </span>
        <span className="shrink-0 text-xs text-neutral-400">▦</span>
      </button>

      {open && (
        <div className={`${POPOVER} w-72 p-3`}>
          <div className="mb-2 flex items-center justify-between">
            <button
              type="button"
              onClick={() => shiftMonth(-1)}
              className="rounded-lg px-2 py-1 text-sm hover:bg-neutral-100 dark:hover:bg-neutral-800"
            >
              ‹
            </button>
            <span className="text-sm font-medium">
              {MONTHS[month]} {year}
            </span>
            <button
              type="button"
              onClick={() => shiftMonth(1)}
              className="rounded-lg px-2 py-1 text-sm hover:bg-neutral-100 dark:hover:bg-neutral-800"
            >
              ›
            </button>
          </div>

          <div className="mb-1 grid grid-cols-7 gap-0.5 text-center text-[10px] text-neutral-400">
            {WEEKDAYS.map((day) => (
              <span key={day}>{day}</span>
            ))}
          </div>

          <div className="grid grid-cols-7 gap-0.5">
            {cells.map((date, index) =>
              date === null ? (
                <span key={`empty-${index}`} />
              ) : (
                <button
                  key={date}
                  type="button"
                  onClick={() => emit(date)}
                  className={`aspect-square rounded-lg text-xs transition ${
                    date === selected
                      ? 'bg-neutral-900 font-medium text-white dark:bg-white dark:text-neutral-900'
                      : date === today
                        ? 'bg-neutral-100 dark:bg-neutral-800'
                        : 'hover:bg-neutral-100 dark:hover:bg-neutral-800'
                  }`}
                >
                  {Number(date.slice(8))}
                </button>
              ),
            )}
          </div>

          <div className="mt-2 flex justify-between border-t border-neutral-200 pt-2 text-xs dark:border-neutral-800">
            <button
              type="button"
              onClick={() => emit(today)}
              className="rounded px-2 py-1 hover:bg-neutral-100 dark:hover:bg-neutral-800"
            >
              Сегодня
            </button>
            <button
              type="button"
              onClick={() => emit('')}
              className="rounded px-2 py-1 text-neutral-500 hover:bg-neutral-100 dark:hover:bg-neutral-800"
            >
              Очистить
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ───────────────────────────── Флажок ─────────────────────────────

/** Квадрат с галочкой вместо системного чекбокса — тот не красится в тёмной теме. */
export function Checkbox({
  checked,
  onChange,
  disabled,
  label,
  className,
}: {
  checked?: boolean;
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
  disabled?: boolean;
  label?: ReactNode;
  className?: string;
}) {
  const toggle = () => {
    if (disabled) return;
    onChange?.({
      target: { checked: !checked },
    } as unknown as React.ChangeEvent<HTMLInputElement>);
  };

  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={Boolean(checked)}
      disabled={disabled}
      onClick={toggle}
      className={`flex items-center gap-2 text-left text-sm disabled:opacity-50 ${className ?? ''}`}
    >
      <span
        className={`flex size-4 shrink-0 items-center justify-center rounded border transition ${
          checked
            ? 'border-neutral-900 bg-neutral-900 text-white dark:border-white dark:bg-white dark:text-neutral-900'
            : 'border-neutral-300 dark:border-neutral-600'
        }`}
      >
        {checked && <span className="text-[10px] leading-none">✓</span>}
      </span>
      {label}
    </button>
  );
}

// ──────────────────────────── Выбор цвета ────────────────────────────

const PRESET_COLORS = [
  '#FFFFFF',
  '#000000',
  '#9E9E9E',
  '#5D4037',
  '#D32F2F',
  '#C2185B',
  '#7B1FA2',
  '#1976D2',
  '#0097A7',
  '#388E3C',
  '#FBC02D',
  '#F57C00',
  '#B8860B',
  '#8D6E63',
  '#455A64',
  '#E91E63',
];

/** Образец с палитрой и полем HEX — системный `type="color"` выглядит инородно. */
export function ColorField({
  value,
  onChange,
  className,
}: {
  value: string;
  onChange: (hex: string) => void;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const ref = useDismiss(open, () => setOpen(false));

  return (
    <div ref={ref} className={`relative ${className ?? ''}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`${FIELD} flex items-center gap-2 text-left`}
      >
        <span
          className="size-5 shrink-0 rounded border border-neutral-300 dark:border-neutral-600"
          style={{ background: value }}
        />
        <span className="font-mono text-xs uppercase">{value}</span>
      </button>

      {open && (
        <div className={`${POPOVER} w-56 p-3`}>
          <div className="grid grid-cols-8 gap-1.5">
            {PRESET_COLORS.map((hex) => (
              <button
                key={hex}
                type="button"
                title={hex}
                onClick={() => {
                  onChange(hex);
                  setOpen(false);
                }}
                className={`size-5 rounded border transition hover:scale-110 ${
                  hex.toUpperCase() === value.toUpperCase()
                    ? 'border-neutral-900 dark:border-white'
                    : 'border-neutral-200 dark:border-neutral-700'
                }`}
                style={{ background: hex }}
              />
            ))}
          </div>

          <div className="mt-3 flex items-center gap-2">
            <input
              value={value}
              onChange={(e) => onChange(e.target.value)}
              placeholder="#000000"
              className="w-full rounded-lg border border-neutral-300 px-2 py-1 font-mono text-xs uppercase outline-none dark:border-neutral-700 dark:bg-neutral-950"
            />
          </div>
        </div>
      )}
    </div>
  );
}

// ──────────────────────────── Кнопки ────────────────────────────

type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'ghost';

const BUTTON_STYLES: Record<ButtonVariant, string> = {
  primary:
    'bg-neutral-900 text-white hover:bg-neutral-800 dark:bg-white dark:text-neutral-900 dark:hover:bg-neutral-200',
  secondary:
    'border border-neutral-300 hover:bg-neutral-100 dark:border-neutral-700 dark:hover:bg-neutral-800',
  danger: 'bg-red-600 text-white hover:bg-red-700',
  ghost: 'hover:bg-neutral-100 dark:hover:bg-neutral-800',
};

export function Button({
  variant = 'primary',
  className,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & { variant?: ButtonVariant }) {
  return (
    <button
      {...props}
      className={`rounded-lg px-3.5 py-2 text-sm font-medium transition disabled:opacity-50 ${BUTTON_STYLES[variant]} ${className ?? ''}`}
    />
  );
}

// ──────────────────────────── Пагинация ────────────────────────────

const PAGE_SIZES = [20, 40, 60];

/**
 * Номера страниц с пропусками: 1 … 5 6 7 … 20.
 * До семи страниц показываем все — многоточие там только мешает.
 */
function pageNumbers(page: number, pages: number): Array<number | 'gap'> {
  if (pages <= 7) return Array.from({ length: pages }, (_, i) => i + 1);

  const near = [page - 1, page, page + 1].filter((n) => n > 1 && n < pages);
  const out: Array<number | 'gap'> = [];
  let previous = 0;

  for (const n of [1, ...near, pages]) {
    if (n - previous > 1) out.push('gap');
    out.push(n);
    previous = n;
  }

  return out;
}

export function Pagination({
  page,
  pages,
  total,
  limit,
  onPage,
  onLimit,
  sizes = PAGE_SIZES,
}: {
  page: number;
  pages: number;
  total: number;
  limit: number;
  onPage: (page: number) => void;
  onLimit: (limit: number) => void;
  sizes?: number[];
}) {
  if (total === 0) return null;

  const from = (page - 1) * limit + 1;
  const to = Math.min(page * limit, total);

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 text-sm">
      <span className="text-neutral-500">
        {from}–{to} из {total}
      </span>

      {pages > 1 && (
        <div className="flex items-center gap-1">
          <Button variant="secondary" disabled={page <= 1} onClick={() => onPage(page - 1)}>
            Назад
          </Button>

          {pageNumbers(page, pages).map((n, index) =>
            n === 'gap' ? (
              <span key={`gap-${index}`} className="px-1 text-neutral-400">
                …
              </span>
            ) : (
              <button
                key={n}
                onClick={() => onPage(n)}
                aria-current={n === page ? 'page' : undefined}
                className={`min-w-9 rounded-lg px-2.5 py-2 font-medium transition ${
                  n === page
                    ? 'bg-neutral-900 text-white dark:bg-white dark:text-neutral-900'
                    : 'hover:bg-neutral-100 dark:hover:bg-neutral-800'
                }`}
              >
                {n}
              </button>
            ),
          )}

          <Button variant="secondary" disabled={page >= pages} onClick={() => onPage(page + 1)}>
            Вперёд
          </Button>
        </div>
      )}

      <label className="flex items-center gap-2 whitespace-nowrap text-neutral-500">
        На странице
        <Select
          value={String(limit)}
          onChange={(event) => onLimit(Number(event.target.value))}
          className="w-20"
        >
          {sizes.map((n) => (
            <option key={n} value={String(n)}>
              {n}
            </option>
          ))}
        </Select>
      </label>
    </div>
  );
}

export function Card({
  title,
  actions,
  children,
}: {
  title?: string;
  actions?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="rounded-xl border border-neutral-200 dark:border-neutral-800">
      {(title || actions) && (
        <header className="flex items-center justify-between gap-3 border-b border-neutral-200 px-4 py-3 dark:border-neutral-800">
          {title && <h2 className="text-sm font-semibold">{title}</h2>}
          {actions}
        </header>
      )}
      <div className="p-4">{children}</div>
    </section>
  );
}

export function Modal({
  open,
  title,
  onClose,
  children,
}: {
  open: boolean;
  title: string;
  onClose: () => void;
  children: ReactNode;
}) {
  useEffect(() => {
    if (!open) return;

    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-auto bg-black/40 p-4 backdrop-blur-sm sm:p-8"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg rounded-2xl border border-neutral-200 bg-white p-6 shadow-xl dark:border-neutral-800 dark:bg-neutral-900"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-base font-semibold">{title}</h2>
          <button
            onClick={onClose}
            className="text-neutral-400 transition hover:text-neutral-700 dark:hover:text-neutral-200"
            aria-label="Закрыть"
          >
            ✕
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

/** Подтверждение необратимого действия: выход, удаление, публикация. */
export function ConfirmDialog({
  open,
  title,
  description,
  confirmLabel = 'Подтвердить',
  cancelLabel = 'Отмена',
  tone = 'primary',
  pending,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  title: string;
  description?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: 'primary' | 'danger';
  pending?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <Modal open={open} title={title} onClose={onCancel}>
      <div className="space-y-5">
        {description && <div className="text-sm text-neutral-500">{description}</div>}

        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onCancel}>
            {cancelLabel}
          </Button>
          <Button variant={tone} disabled={pending} onClick={onConfirm}>
            {pending ? 'Выполняем…' : confirmLabel}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

export function Badge({
  tone = 'neutral',
  title,
  children,
}: {
  tone?: 'neutral' | 'green' | 'red' | 'amber';
  title?: string;
  children: ReactNode;
}) {
  const tones = {
    neutral: 'bg-neutral-100 text-neutral-700 dark:bg-neutral-800 dark:text-neutral-300',
    green: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-400',
    red: 'bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-400',
    amber: 'bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-400',
  };

  return (
    <span title={title} className={`rounded px-1.5 py-0.5 text-xs font-medium ${tones[tone]}`}>
      {children}
    </span>
  );
}

export function ErrorText({ children }: { children: ReactNode }) {
  if (!children) return null;

  return (
    <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600 dark:bg-red-950/40 dark:text-red-400">
      {children}
    </p>
  );
}

export type Localized = { uz?: string; ru?: string; en?: string };

/** Поле, которое заполняется на трёх языках. Русский обязателен по умолчанию. */
export function LocalizedInput({
  label,
  value,
  onChange,
  multiline,
}: {
  label: string;
  value: Localized;
  onChange: (next: Localized) => void;
  multiline?: boolean;
}) {
  const Control = multiline ? Textarea : Input;

  return (
    <div className="space-y-1.5">
      <span className="text-sm font-medium">{label}</span>
      <div className="grid gap-2 sm:grid-cols-3">
        {(['ru', 'uz', 'en'] as const).map((locale) => (
          <div key={locale}>
            <Control
              placeholder={{ ru: 'Русский', uz: "O'zbekcha", en: 'English' }[locale]}
              value={value[locale] ?? ''}
              rows={multiline ? 3 : undefined}
              onChange={(e: React.ChangeEvent<HTMLInputElement & HTMLTextAreaElement>) =>
                onChange({ ...value, [locale]: e.target.value })
              }
            />
          </div>
        ))}
      </div>
    </div>
  );
}

/** Суммы в интерфейсе — в сумах, в API уходят тийинами. */
export const toTiyin = (sum: string | number): number => Math.round(Number(sum || 0) * 100);
export const toSum = (tiyin?: number | null): string => (tiyin ? String(tiyin / 100) : '');
export const formatSum = (tiyin?: number | null): string =>
  tiyin === null || tiyin === undefined ? '—' : `${(tiyin / 100).toLocaleString('ru-RU')} сум`;
